import React, { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Badge } from "../ui/badge";
import { Progress } from "../ui/progress";
import { Button } from "../ui/button";
import { Label } from "../ui/label";
import { Input } from "../ui/input";
import {
  Calendar,
  PhilippinePeso,
  Users,
  Building,
  FileText,
  Link,
  Paperclip,
  Plus,
  UserPlus,
  CheckCircle,
  Clock,
  XCircle,
  Eye,
  TrendingUp,
  AlertCircle,
  FolderOpen,
  MessageCircle,
  Search,
  CheckCircle2,
} from "lucide-react";
import { Project, ProjectFeedback, User } from "../../types";
import { CreateProjectForm } from "./CreateProjectForm";
import { ProjectDetails } from "./ProjectDetails";
import { emailService } from "../../utils/emailService";
import { ClientCreationDialog } from "../client/ClientCreationDialog";

interface ProjectsGridProps {
  projects: Project[];
  users: User[];
  currentUser: User;
  onCreateProject?: (
    project: Omit<Project, "id">
  ) => void | Promise<void> | Promise<Project>;
  onAssignFabricator?: (
    projectId: string,
    fabricatorId: string,
    message?: string
  ) => void;
  onUpdateProject?: (project: Project) => void;
  onAcceptAssignment?: (
    assignmentId: string,
    response?: string,
    projectId?: string
  ) => void;
  onDeclineAssignment?: (
    assignmentId: string,
    response?: string,
    projectId?: string
  ) => void;
  onCreateUser?: (user: User) => void;
  onBroadcastFabricators?: (projectId: string, message?: string) => void;
}

export function ProjectsGrid({
  projects = [],
  users = [],
  currentUser,
  onCreateProject,
  onAssignFabricator: _onAssignFabricator,
  onUpdateProject,
  onAcceptAssignment,
  onDeclineAssignment,
  onCreateUser,
  onBroadcastFabricators,
}: ProjectsGridProps) {
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [selectedProject, setSelectedProject] = useState<Project | null>(null);
  const [showProjectDetails, setShowProjectDetails] = useState(false);
  const [showClientDialog, setShowClientDialog] = useState(false);
  const [clientDialogProject, setClientDialogProject] = useState<Project | null>(
    null
  );
  const [localClientAssignedProjectIds, setLocalClientAssignedProjectIds] =
    useState<Set<string>>(new Set());
  const [selectedAssignment, setSelectedAssignment] = useState<string | null>(
    null
  );

  // ─── Search state ───────────────────────────────────────────────
  const [searchTerm, setSearchTerm] = useState("");

  if (!currentUser) return null;

  // Role-based initial filtering
  const getRoleFilteredProjects = () => {
    if (currentUser.role === "admin") {
      return projects;
    }
    if (currentUser.role === "supervisor") {
      return projects.filter(
        (p) =>
          p.supervisorId === currentUser.id ||
          (p.pendingSupervisors &&
            p.pendingSupervisors.includes(currentUser.id))
      );
    }
    if (currentUser.role === "fabricator") {
      return projects.filter(
        (p) =>
          p.fabricatorIds.includes(currentUser.id) ||
          p.pendingAssignments?.some(
            (assignment) =>
              assignment.fabricatorId === currentUser.id &&
              assignment.status === "pending"
          )
      );
    }
    return projects.filter((p) => p.fabricatorIds.includes(currentUser.id));
  };

  const roleFilteredProjects = getRoleFilteredProjects();

  // Client-side search filtering
  const filteredProjects = roleFilteredProjects
    .filter((p) => p.status !== "completed" && p.progress < 100)
    .filter((project) => {
      if (!searchTerm.trim()) return true;

      const term = searchTerm.toLowerCase().trim();

      const supervisorName =
        users
          .find((u) => u.id === project.supervisorId)
          ?.name?.toLowerCase() || "";

      const fabricatorNames = project.fabricatorIds
        .map((id) => users.find((u) => u.id === id)?.name?.toLowerCase() || "")
        .join(" ");

      return (
        project.name.toLowerCase().includes(term) ||
        (project.description || "").toLowerCase().includes(term) ||
        (project.clientName || "").toLowerCase().includes(term) ||
        supervisorName.includes(term) ||
        fabricatorNames.includes(term)
      );
    });

  const totalProjectsCount = roleFilteredProjects.length;
  const totalCompletedProjectsCount = roleFilteredProjects.filter(
    (project) => project.status === "completed" || project.progress >= 100,
  ).length;

  const canCreateProject =
    currentUser.role === "admin" || currentUser.role === "supervisor";

  const getStatusColor = (status: string) => {
    switch (status) {
      case "pending_approval":
        return "secondary";
      case "revision_required":
        return "destructive";
      case "pending_completion_approval":
        return "secondary";
      case "0_Created":
        return "outline";
      case "1_Assigned_to_FAB":
        return "secondary";
      case "pending-assignment":
        return "destructive";
      case "in-progress":
        return "default";
      case "2_Ready_for_Supervisor_Review":
        return "destructive";
      case "3_Ready_for_Admin_Review":
        return "destructive";
      case "4_Ready_for_Client_Signoff":
        return "default";
      default:
        return "outline";
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case "urgent":
        return "destructive";
      case "high":
        return "destructive";
      case "medium":
        return "secondary";
      case "low":
        return "outline";
      default:
        return "outline";
    }
  };

  const getSupervisorLabel = (project: Project) => {
    if (project.supervisorId) {
      const supervisor = users.find((u) => u.id === project.supervisorId);
      return supervisor?.name || "Unknown Supervisor";
    }

    if (project.pendingSupervisors && project.pendingSupervisors.length > 0) {
      return "Pending supervisor acceptance";
    }

    return "Not assigned";
  };

  const getFabricatorNames = (fabricatorIds: string[]) => {
    return fabricatorIds
      .map((id) => users.find((u) => u.id === id)?.name || "Unknown")
      .join(", ");
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  };

  const formatFeedbackDate = (value?: string) =>
    value
      ? new Date(value).toLocaleDateString(undefined, {
          year: "numeric",
          month: "short",
          day: "numeric",
        })
      : "TBD";

  const getFabricatorBudget = (project: Project, fabricatorId: string) => {
    return project.fabricatorBudgets?.find(
      (fb) => fb.fabricatorId === fabricatorId
    );
  };

  const supervisorProjectScope =
    currentUser.role === "supervisor"
      ? projects.filter(
          (project) =>
            project.supervisorId === currentUser.id ||
            project.pendingSupervisors?.includes(currentUser.id)
        )
      : [];

  const feedbackPool =
    currentUser.role === "admin"
      ? projects
      : currentUser.role === "supervisor"
      ? supervisorProjectScope
      : [];

  const latestFeedbackEntries: (ProjectFeedback & { projectName: string })[] =
    feedbackPool
      .flatMap((project) =>
        (project.feedbackEntries ?? []).map((entry) => ({
          ...entry,
          projectName: project.name,
        }))
      )
      .sort(
        (a, b) =>
          new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
      )
      .slice(0, 4);

  const handleCreateProject = async (project: Omit<Project, "id">) => {
    if (onCreateProject) {
      await onCreateProject(project);
    }
  };

  const handleViewDetails = (project: Project) => {
    setSelectedProject(project);
    setShowProjectDetails(true);
  };

  const handleUpdateProject = (updatedProject: Project) => {
    if (onUpdateProject) {
      onUpdateProject(updatedProject);
      emailService.sendProjectUpdate(
        updatedProject,
        users,
        "progress_update",
        currentUser
      );
    }
  };

  const isClientAssigned = (project: Project) => {
    if (localClientAssignedProjectIds.has(project.id)) return true;
    if (project.clientName && project.clientName.trim().length > 0) return true;
    return users.some(
      (u) => u.role === "client" && u.clientProjectId === project.id
    );
  };

  const handleTransition = (
    project: Project,
    nextStatus: Project["status"]
  ) => {
    if (!onUpdateProject) return;
    const updatedProject = { ...project, status: nextStatus };
    onUpdateProject(updatedProject);
    emailService.sendProjectUpdate(
      updatedProject,
      users,
      "status_change",
      currentUser
    );
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <div className="flex items-center gap-3">
            <FolderOpen className="h-6 w-6 text-gray-500 dark:text-gray-400" />
            <h1 className="text-3xl font-bold tracking-tight">
              Projects
            </h1>
          </div>
          <p className="mt-2 text-sm text-muted-foreground">
            Manage and track your projects
          </p>
        </div>

        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:gap-4 w-full sm:w-auto">
          <div className="flex flex-col gap-2 sm:flex-row sm:gap-3">
            {(currentUser.role === "admin" ||
              currentUser.role === "supervisor") && (
              <div className="w-full sm:w-auto whitespace-nowrap text-sm text-muted-foreground flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-border/60 bg-muted/40 font-medium">
                <CheckCircle2 className="h-4 w-4 text-primary" />
                <span>Completed: <strong>{projects.filter((p) => p.status === "completed" || p.progress >= 100).length}</strong></span>
              </div>
            )}

            {canCreateProject && (
              <Button
                onClick={() => setShowCreateForm(true)}
                variant="default"
                size="default"
                className="w-full sm:w-auto shadow-sm"
              >
                <Plus className="h-4 w-4 mr-1.5" />
                Create Project
              </Button>
            )}
          </div>
        </div>
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        <Card className="overflow-hidden border border-border/80 bg-card text-card-foreground shadow-xs">
          <CardContent className="p-5">
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                  Total Projects
                </p>
                <div className="mt-1 text-3xl font-bold tracking-tight text-foreground font-manrope">
                  {totalProjectsCount}
                </div>
                <p className="mt-3 text-xs text-muted-foreground">
                  All active projects available in view
                </p>
              </div>
              <div className="flex h-11 w-11 items-center justify-center rounded-lg border border-border bg-muted text-foreground">
                <FolderOpen className="h-5 w-5" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="overflow-hidden border border-border/80 bg-card text-card-foreground shadow-xs">
          <CardContent className="p-5">
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                  Completed Projects
                </p>
                <div className="mt-1 text-3xl font-bold tracking-tight text-foreground font-manrope">
                  {totalCompletedProjectsCount}
                </div>
                <p className="mt-3 text-xs text-muted-foreground">
                  Projects marked completed or 100% finished
                </p>
              </div>
              <div className="flex h-11 w-11 items-center justify-center rounded-lg border border-border bg-muted text-foreground">
                <CheckCircle2 className="h-5 w-5" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {(currentUser.role === "admin" ||
        currentUser.role === "supervisor") && (
        <Card className="overflow-hidden border border-border/80 bg-card text-card-foreground shadow-xs">
          <CardHeader className="flex items-center justify-between border-b border-border/60 p-5">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg border border-border bg-muted text-foreground">
                <MessageCircle className="h-5 w-5" />
              </div>
              <div>
                <CardTitle className="text-base font-bold tracking-tight text-foreground">
                  Client Feedback
                </CardTitle>
                <p className="mt-0.5 text-xs text-muted-foreground">
                  Recent reviews and sign-off comments
                </p>
              </div>
            </div>
            <Badge
              variant="outline"
              className="px-2.5 py-0.5 text-xs font-semibold"
            >
              {latestFeedbackEntries.length} recent
            </Badge>
          </CardHeader>
          <CardContent className="p-5">
            <div className="max-h-[260px] space-y-3 overflow-y-auto pr-1">
              {latestFeedbackEntries.length === 0 ? (
                <div className="rounded-lg border border-dashed border-border bg-muted/30 px-4 py-8 text-center text-sm text-muted-foreground">
                  No client feedback has been recorded for your projects yet.
                </div>
              ) : (
                latestFeedbackEntries.map((entry) => (
                  <div
                    key={`${entry.id}-${entry.projectId}`}
                    className="space-y-2 rounded-lg border border-border/80 bg-card/60 p-4"
                  >
                    <div>
                      <p className="text-sm font-medium leading-relaxed text-foreground">
                        {entry.comment}
                      </p>
                    </div>
                    <div className="flex items-center justify-between gap-3 text-[0.7rem] font-semibold uppercase tracking-wider text-muted-foreground">
                      <span className="truncate">{entry.projectName}</span>
                      <span className="shrink-0">{formatFeedbackDate(entry.createdAt)}</span>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      {entry.createdByName || entry.createdBy || "Client"}{" "}
                      <span className="text-[10px] uppercase tracking-wider text-muted-foreground/80">
                        ({entry.createdByRole || "client"})
                      </span>
                    </p>
                  </div>
                ))
              )}
            </div>
          </CardContent>
        </Card>
      )}

      <div className="relative flex-1 sm:min-w-[280px]">
        <Search className="pointer-events-none absolute left-3 top-4.5 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          type="search"
          placeholder="Search project..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full max-w-md pl-10"
        />
        <p className="mt-1 text-sm text-muted-foreground">
          <span className="font-medium text-[#e28a33]">Note:</span> You can
          search by name, client, or supervisor
        </p>
      </div>

      <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
        {filteredProjects.map((project) => (
          <Card
            key={project.id}
            className="group overflow-hidden rounded-xl border border-border/80 bg-card text-card-foreground shadow-xs transition-all hover:border-border hover:shadow-md"
          >
            <div className="h-1.5 bg-primary"></div>
            <CardHeader className="border-b border-border/60 bg-card/50 p-5 sm:p-6">
              <div className="space-y-2">
                <CardTitle className="text-lg font-bold tracking-tight text-foreground">
                  {project.name}
                </CardTitle>
                <div className="flex flex-wrap gap-2">
                  <Badge
                    variant={getStatusColor(project.status)}
                    className="rounded-md px-2.5 py-0.5 text-[11px] font-semibold uppercase tracking-wider"
                  >
                    {project.status === "pending_approval"
                      ? "Pending Approval"
                      : project.status === "revision_required"
                      ? "Revision Required"
                      : project.status === "pending_completion_approval"
                      ? "Awaiting Completion Sign-off"
                      : project.status === "pending-assignment"
                      ? "Broadcasting"
                      : project.status === "in-progress"
                      ? "In Progress"
                      : project.status.replace(/_/g, " ")}
                  </Badge>
                  <Badge
                    variant={getPriorityColor(project.priority)}
                    className="rounded-md px-2.5 py-0.5 text-[11px] font-semibold uppercase tracking-wider"
                  >
                    {project.priority}
                  </Badge>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-5 p-5 sm:p-6">
              <p className="text-sm leading-relaxed text-muted-foreground line-clamp-3">
                {project.description}
              </p>

              <div className="rounded-lg border border-border/60 bg-muted/40 p-4">
                <div className="mb-2.5 flex justify-between text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                  <span>Phase / Progress</span>
                  <span className="font-bold text-foreground">
                    {project.progress === 0
                      ? "Not Started"
                      : project.progress >= 100 || project.status === "completed"
                      ? "Completed"
                      : project.status === "0_Created"
                      ? "Planning Phase"
                      : project.status === "1_Assigned_to_FAB"
                      ? "Fabrication Phase"
                      : project.status === "2_Ready_for_Supervisor_Review"
                      ? "Supervisor Review Phase"
                      : project.status === "3_Ready_for_Admin_Review"
                      ? "Admin Review Phase"
                      : project.status === "4_Ready_for_Client_Signoff"
                      ? "Client Sign-off Phase"
                      : "In Progress Phase"}
                  </span>
                </div>
                <Progress value={project.progress} className="h-2" />
              </div>

              {/* Role-based financial information */}
              {currentUser.role === "fabricator" && (
                <div className="bg-secondary/20 p-2 rounded mt-2">
                  {(() => {
                    const fabricatorBudget = getFabricatorBudget(
                      project,
                      currentUser.id
                    );
                    if (fabricatorBudget) {
                      return (
                        <div className="space-y-1">
                          <div className="flex justify-between items-center text-xs">
                            <span className="text-muted-foreground">
                              My Budget:
                            </span>
                            <span className="font-medium">
                              ₱
                              {fabricatorBudget.allocatedAmount.toLocaleString()}
                            </span>
                          </div>
                          <div className="flex justify-between items-center text-xs">
                            <span className="text-muted-foreground">
                              Spent:
                            </span>
                            <span>
                              ₱{fabricatorBudget.spentAmount.toLocaleString()}
                            </span>
                          </div>
                        </div>
                      );
                    } else {
                      return (
                        <div className="flex items-center gap-2 text-xs">
                          <PhilippinePeso className="h-3 w-3" />
                          <span>
                            Labor Pool: ₱
                            {Number(
                              project.fabricatorAllocation || 0
                            ).toLocaleString()}
                          </span>
                        </div>
                      );
                    }
                  })()}
                </div>
              )}

              {currentUser.role === "supervisor" && (
                <div className="bg-muted/30 p-2 rounded text-xs space-y-1 mt-2 border border-border">
                  <div className="flex justify-between font-semibold border-b pb-1 mb-1">
                    <span>Total Budget:</span>
                    <span>₱{Number(project.budget).toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Materials:</span>
                    <span>
                      ₱
                      {Number(
                        project.materialsAllocation || 0
                      ).toLocaleString()}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Labor:</span>
                    <span>
                      ₱
                      {Number(
                        project.fabricatorAllocation || 0
                      ).toLocaleString()}
                    </span>
                  </div>
                  <div className="flex justify-between pt-1 border-t mt-1">
                    <span className="text-muted-foreground">
                      Total Spent:
                    </span>
                    <span className="text-gray-600">
                      ₱{Number(project.spent).toLocaleString()}
                    </span>
                  </div>
                </div>
              )}

              {currentUser.role === "admin" && (
                <div className="bg-muted/30 p-2 rounded text-xs space-y-1 mt-2">
                  <div className="flex justify-between items-center">
                    <span className="text-muted-foreground flex items-center gap-1">
                      <TrendingUp className="h-3 w-3" /> Revenue:
                    </span>
                    <span className="font-medium text-green-700 dark:text-green-400">
                      ₱{Number(project.revenue).toLocaleString()}
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-muted-foreground">Est. Cost:</span>
                    <span>₱{Number(project.budget).toLocaleString()}</span>
                  </div>
                  <div className="border-t border-dashed my-1"></div>
                  <div className="flex justify-between items-center font-semibold">
                    <span>Est. Profit:</span>
                    <span
                      className={
                        Number(project.revenue) - Number(project.budget) >= 0
                          ? "text-green-600"
                          : "text-destructive"
                      }
                    >
                      ₱
                      {(
                        Number(project.revenue) - Number(project.budget)
                      ).toLocaleString()}
                    </span>
                  </div>
                  {Number(project.spent) > Number(project.budget) && (
                    <div className="flex items-center gap-1 text-destructive mt-1 justify-end">
                      <AlertCircle className="h-3 w-3" />
                      <span>Over Budget</span>
                    </div>
                  )}
                </div>
              )}

              <div className="grid gap-3 md:grid-cols-2">
                <div className="flex items-center gap-3 rounded-sm border border-border bg-muted px-4 py-3 text-sm text-muted-foreground dark:border-[#333333] dark:bg-[#111111]">
                  <Building className="h-4 w-4 text-slate-400 dark:text-slate-500" />
                  <span className="truncate">
                    Client: {project.clientName || "Not assigned"}
                  </span>
                </div>

                <div className="flex items-center gap-3 rounded-sm border border-border bg-muted px-4 py-3 text-sm text-muted-foreground dark:border-[#333333] dark:bg-[#111111]">
                  <Calendar className="h-4 w-4 text-slate-400 dark:text-slate-500" />
                  <span className="truncate">
                    {new Date(project.startDate).toLocaleDateString()} -{" "}
                    {new Date(project.endDate).toLocaleDateString()}
                  </span>
                </div>

                <div className="flex items-center gap-3 rounded-sm border border-border bg-muted px-4 py-3 text-sm text-muted-foreground dark:border-[#333333] dark:bg-[#111111]">
                  <Users className="h-4 w-4 text-slate-400 dark:text-slate-500" />
                  <span className="truncate">
                    Supervisor: {getSupervisorLabel(project)}
                  </span>
                </div>

                <div className="flex items-center gap-3 rounded-sm border border-border bg-muted px-4 py-3 text-sm text-muted-foreground dark:border-[#333333] dark:bg-[#111111]">
                  <Users className="h-4 w-4 text-slate-400 dark:text-slate-500" />
                  <span className="truncate">
                    Team: {getFabricatorNames(project.fabricatorIds) || "None"}
                  </span>
                </div>
              </div>

              {project.documentationUrl && (
                <div className="flex items-center gap-3 rounded-sm border border-border bg-muted px-4 py-3 text-sm text-muted-foreground dark:border-[#333333] dark:bg-[#111111]">
                  <Link className="h-4 w-4 text-slate-400 dark:text-slate-500" />
                  <a
                    href={project.documentationUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary hover:underline truncate"
                  >
                    Google Drive Documentation
                  </a>
                </div>
              )}

              {project.attachments && project.attachments.length > 0 && (
                <div className="space-y-2 rounded-sm border border-border bg-muted p-4 dark:border-[#333333] dark:bg-[#111111]">
                  <div className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-300">
                    <Paperclip className="h-4 w-4 text-slate-400 dark:text-slate-500" />
                    <span>Attachments ({project.attachments.length})</span>
                  </div>
                  <div className="ml-1 space-y-2">
                    {project.attachments.slice(0, 2).map((attachment) => (
                      <div
                        key={attachment.id}
                        className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-300"
                      >
                        <FileText className="h-4 w-4 text-slate-400 dark:text-slate-500" />
                        <span className="truncate">{attachment.name}</span>
                        <span className="text-slate-400 dark:text-slate-500">
                          ({formatFileSize(attachment.size)})
                        </span>
                      </div>
                    ))}
                    {project.attachments.length > 2 && (
                      <div className="ml-6 text-sm text-slate-400 dark:text-slate-500">
                        +{project.attachments.length - 2} more
                      </div>
                    )}
                  </div>
                </div>
              )}

              <div className="flex items-center gap-3 rounded-sm border border-border bg-muted px-4 py-3 text-sm text-muted-foreground dark:border-[#333333] dark:bg-[#111111]">
                <Users className="h-4 w-4 text-slate-400 dark:text-slate-500" />
                <span>
                  Created by:{" "}
                  {users.find((u) => u.id === project.createdBy)?.name ||
                    "Unknown"}
                </span>
              </div>

              {/* ─── ADMIN: Project Approval Queue Banner ─── */}
              {currentUser.role === "admin" &&
                project.status === "pending_approval" && (
                  <div className="border-2 border-border rounded-sm p-4 space-y-3 bg-muted/30">
                    <div className="flex items-center gap-2">
                      <CheckCircle2 className="h-4 w-4" />
                      <span className="font-semibold text-sm">Project Pitch — Awaiting Approval</span>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      Pitched by{" "}
                      <strong>
                        {users.find((u) => u.id === project.createdBy)?.name || "Unknown"}
                      </strong>
                    </p>
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        className="flex-1 bg-primary text-primary-foreground hover:bg-secondary"
                        onClick={() => handleTransition(project, "0_Created")}
                      >
                        <CheckCircle className="h-4 w-4 mr-1" />
                        Approve
                      </Button>
                      <Button
                        size="sm"
                        variant="destructive"
                        className="flex-1"
                        onClick={() => handleTransition(project, "revision_required")}
                      >
                        <XCircle className="h-4 w-4 mr-1" />
                        Request Revision
                      </Button>
                    </div>
                  </div>
                )}

              {/* ─── SUPERVISOR: Revision Required Banner ─── */}
              {currentUser.role === "supervisor" &&
                project.createdBy === currentUser.id &&
                project.status === "revision_required" && (
                  <div className="border-2 border-destructive/50 rounded-sm p-4 space-y-3">
                    <div className="flex items-center gap-2">
                      <AlertCircle className="h-4 w-4 text-destructive" />
                      <span className="font-semibold text-sm text-destructive">Revision Requested</span>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      CEO/Admin has requested changes to this project pitch. Edit the project and resubmit.
                    </p>
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        className="flex-1"
                        onClick={() => handleViewDetails(project)}
                      >
                        Edit Project
                      </Button>
                      <Button
                        size="sm"
                        className="flex-1 bg-primary text-primary-foreground hover:bg-secondary"
                        onClick={() => handleTransition(project, "pending_approval")}
                      >
                        Resubmit
                      </Button>
                    </div>
                  </div>
                )}

              {/* ─── SUPERVISOR: Submit for Completion Approval ─── */}
              {currentUser.role === "supervisor" &&
                project.supervisorId === currentUser.id &&
                project.progress >= 100 &&
                project.status !== "pending_completion_approval" &&
                project.status !== "completed" && (
                  <Button
                    size="sm"
                    variant="outline"
                    className="w-full"
                    onClick={() => handleTransition(project, "pending_completion_approval")}
                  >
                    <CheckCircle className="h-4 w-4 mr-2" />
                    Submit for Completion Approval
                  </Button>
                )}

              {/* ─── ADMIN: Completion Sign-off Banner ─── */}
              {currentUser.role === "admin" &&
                project.status === "pending_completion_approval" && (
                  <div className="border-2 border-border rounded-sm p-4 space-y-3 bg-muted/30">
                    <div className="flex items-center gap-2">
                      <CheckCircle2 className="h-4 w-4" />
                      <span className="font-semibold text-sm">Project Completion — Final Sign-off</span>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      Supervisor has marked this project complete. Approve to close it out.
                    </p>
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        className="flex-1 bg-primary text-primary-foreground hover:bg-secondary"
                        onClick={() => handleTransition(project, "completed")}
                      >
                        <CheckCircle className="h-4 w-4 mr-1" />
                        Approve Completion
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        className="flex-1"
                        onClick={() => handleTransition(project, "in-progress")}
                      >
                        Send Back
                      </Button>
                    </div>
                  </div>
                )}

              {/* Pending Supervisor Assignment Banner */}
              {currentUser.role === "supervisor" &&
                project.pendingSupervisors?.includes(currentUser.id) && (
                  <div className="bg-muted dark:bg-[#111111]/20 border-2 border-border dark:border-blue-700 rounded-sm p-4 space-y-3 ">
                    <div className="flex items-center gap-2">
                      <Clock className="h-4 w-4 text-primary dark:text-muted-foreground" />
                      <span className="font-semibold text-blue-900 dark:text-blue-100">
                        Pending Project Assignment
                      </span>
                    </div>
                    <p className="text-sm text-blue-800 dark:text-blue-200">
                      You have been invited to supervise this project.
                    </p>
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        className="flex-1 bg-green-600 hover:bg-green-700 text-white"
                        onClick={() =>
                          onAcceptAssignment &&
                          onAcceptAssignment("", "accepted", project.id)
                        }
                      >
                        <CheckCircle className="h-4 w-4 mr-2" />
                        Accept
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        className="flex-1 border-red-300 text-red-600 hover:bg-red-50"
                        onClick={() =>
                          onDeclineAssignment &&
                          onDeclineAssignment("", "declined", project.id)
                        }
                      >
                        <XCircle className="h-4 w-4 mr-2" />
                        Decline
                      </Button>
                    </div>
                  </div>
                )}

              {/* ─── PENDING ASSIGNMENT BANNER FOR FABRICATORS ─── */}
              {currentUser.role === "fabricator" &&
                project.pendingAssignments?.some(
                  (assignment) =>
                    assignment.fabricatorId === currentUser.id &&
                    assignment.status === "pending"
                ) &&
                !project.fabricatorIds.includes(currentUser.id) && (
                  <div className="bg-gray-50 dark:bg-gray-900/20 border-2 border-gray-300 dark:border-gray-700 rounded-sm p-4 space-y-3  animate-in fade-in slide-in-">
                    <div className="flex items-center gap-2">
                      <Clock className="h-4 w-4 text-gray-600 dark:text-gray-500 dark:text-gray-400" />
                      <span className="font-semibold text-gray-900 dark:text-gray-100">
                        New Project Assignment
                      </span>
                    </div>
                    {project.pendingAssignments
                      .filter(
                        (a) =>
                          a.fabricatorId === currentUser.id &&
                          a.status === "pending"
                      )
                      .map((assignment) => {
                        const supervisor = users.find(
                          (u) => u.id === assignment.assignedBy
                        );
                        const isAcceptMode =
                          selectedAssignment === `accept-${assignment.id}`;
                        const isDeclineMode =
                          selectedAssignment === assignment.id &&
                          !selectedAssignment?.includes("accept-");

                        return (
                          <div key={assignment.id} className="space-y-3">
                            <div className="text-xs text-gray-700 dark:text-gray-400 flex items-center gap-2">
                              <Users className="h-3 w-3" />
                              <span>
                                Assigned by:{" "}
                                <strong>
                                  {supervisor?.name || "Unknown Supervisor"}
                                </strong>
                              </span>
                            </div>

                            {/* Default Buttons */}
                            {!isAcceptMode && !isDeclineMode && (
                              <div className="flex gap-2">
                                <Button
                                  size="sm"
                                  className="flex-1 bg-green-600 hover:bg-green-700 text-white"
                                  onClick={() => {
                                    setSelectedAssignment(
                                      `accept-${assignment.id}`
                                    );
                                  }}
                                >
                                  <CheckCircle className="h-4 w-4 mr-2" />
                                  Accept
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="flex-1 border-red-300 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 hover:border-red-400"
                                  onClick={() => {
                                    setSelectedAssignment(assignment.id);
                                  }}
                                >
                                  <XCircle className="h-4 w-4 mr-2" />
                                  Decline
                                </Button>
                              </div>
                            )}

                            {/* Accept Confirmation */}
                            {isAcceptMode && (
                              <div className="space-y-3 bg-green-50 dark:bg-green-900/10 rounded-sm p-3 border border-green-200 dark:border-green-800">
                                <Label className="text-sm font-medium text-green-900 dark:text-green-100">
                                  Confirm Accept
                                </Label>
                                <p className="text-sm text-green-800 dark:text-green-200">
                                  Are you sure you want to take this project?
                                </p>
                                <div className="flex gap-2">
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    className="flex-1"
                                    onClick={() => setSelectedAssignment(null)}
                                  >
                                    Cancel
                                  </Button>
                                  <Button
                                    size="sm"
                                    className="flex-1 bg-green-600 hover:bg-green-700 text-white"
                                    onClick={() => {
                                      if (onAcceptAssignment) {
                                        onAcceptAssignment(
                                          assignment.id,
                                          "accepted",
                                          project.id
                                        );
                                        setSelectedAssignment(null);
                                      }
                                    }}
                                  >
                                    <CheckCircle className="h-4 w-4 mr-2" />
                                    Confirm
                                  </Button>
                                </div>
                              </div>
                            )}

                            {/* Decline Confirmation */}
                            {isDeclineMode && (
                              <div className="space-y-3 bg-red-50 dark:bg-red-900/10 rounded-sm p-3 border border-red-200 dark:border-red-800">
                                <Label className="text-sm font-medium text-red-900 dark:text-red-100">
                                  Confirm Decline
                                </Label>
                                <p className="text-sm text-red-800 dark:text-red-200">
                                  Are you sure you want to decline?
                                </p>
                                <div className="flex gap-2">
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    className="flex-1 dark:text-white"
                                    onClick={() => setSelectedAssignment(null)}
                                  >
                                    Cancel
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="destructive"
                                    className="flex-1 dark:text-white"
                                    onClick={() => {
                                      if (onDeclineAssignment) {
                                        onDeclineAssignment(
                                          assignment.id,
                                          "declined",
                                          project.id
                                        );
                                        setSelectedAssignment(null);
                                      }
                                    }}
                                  >
                                    <XCircle className="h-4 w-4 mr-2" />
                                    Decline
                                  </Button>
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      })}
                  </div>
                )}

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-3 pt-2">
                <Button
                  variant="outline"
                  size="sm"
                  className="w-full font-medium"
                  onClick={() => handleViewDetails(project)}
                >
                  <Eye className="h-3.5 w-3.5 mr-1.5" />
                  <span className="truncate">Details</span>
                </Button>

                {(currentUser.role === "admin" ||
                  currentUser.role === "supervisor") &&
                  (isClientAssigned(project) ? (
                    <Button
                      variant="outline"
                      size="sm"
                      disabled
                      className="w-full text-muted-foreground opacity-70"
                    >
                      <CheckCircle className="h-3.5 w-3.5 mr-1.5" />
                      Client
                    </Button>
                  ) : (
                    <Button
                      variant="outline"
                      size="sm"
                      className="w-full text-foreground hover:bg-muted font-medium"
                      onClick={() => {
                        setClientDialogProject(project);
                        setShowClientDialog(true);
                      }}
                    >
                      <UserPlus className="h-3.5 w-3.5 mr-1.5" />
                      Client
                    </Button>
                  ))}

                {/* BROADCAST BUTTON FOR SUPERVISOR */}
                {currentUser.role === "supervisor" &&
                  project.supervisorId === currentUser.id && (
                    <Button
                      variant="default"
                      size="sm"
                      className="w-full font-medium shadow-xs"
                      onClick={() =>
                        onBroadcastFabricators &&
                        onBroadcastFabricators(project.id)
                      }
                      disabled={
                        project.status === "pending-assignment" ||
                        project.status === "completed"
                      }
                    >
                      <Users className="h-3.5 w-3.5 mr-1.5" />
                      <span className="truncate">
                        {project.status === "pending-assignment"
                          ? "Broadcasting..."
                          : "Broadcast FABs"}
                      </span>
                    </Button>
                  )}

                {currentUser.role === "admin" &&
                  project.status === "0_Created" && (
                    <Button
                      variant="default"
                      size="sm"
                      className="w-full sm:col-span-2 font-medium shadow-xs"
                      onClick={() =>
                        handleTransition(project, "1_Assigned_to_FAB")
                      }
                    >
                      Assign Now
                    </Button>
                  )}

                {currentUser.role === "fabricator" &&
                  (project.status === "1_Assigned_to_FAB" ||
                    project.status === "in-progress") && (
                    <Button
                      variant="default"
                      size="sm"
                      className="w-full sm:col-span-2 font-medium shadow-xs"
                      onClick={() =>
                        handleTransition(
                          project,
                          "2_Ready_for_Supervisor_Review"
                        )
                      }
                      disabled={
                        !(
                          project.documentationUrl ||
                          (project.attachments &&
                            project.attachments.length > 0)
                        )
                      }
                    >
                      Submit Review
                    </Button>
                  )}

                {currentUser.role === "supervisor" &&
                  project.status === "2_Ready_for_Supervisor_Review" && (
                    <>
                      <Button
                        variant="default"
                        size="sm"
                        className="font-medium shadow-xs"
                        onClick={() =>
                          handleTransition(project, "3_Ready_for_Admin_Review")
                        }
                      >
                        Approve
                      </Button>
                      <Button
                        variant="destructive"
                        size="sm"
                        className="font-medium shadow-xs"
                        onClick={() =>
                          handleTransition(project, "1_Assigned_to_FAB")
                        }
                      >
                        Reject
                      </Button>
                    </>
                  )}

                {currentUser.role === "admin" &&
                  project.status === "3_Ready_for_Admin_Review" && (
                    <Button
                      variant="default"
                      size="sm"
                      className="w-full sm:col-span-2 font-medium shadow-xs"
                      onClick={() =>
                        handleTransition(
                          project,
                          "4_Ready_for_Client_Signoff"
                        )
                      }
                    >
                      Final Approval
                    </Button>
                  )}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {filteredProjects.length === 0 && (
        <Card>
          <CardContent className="py-12 text-center">
            <h3 className="text-lg mb-2">No projects found</h3>
            <p className="text-muted-foreground">
              {currentUser.role === "fabricator"
                ? "You have no active or pending projects."
                : "Try adjusting your search terms or create a new project."}
            </p>
          </CardContent>
        </Card>
      )}

      {showCreateForm && (
        <CreateProjectForm
          currentUser={currentUser}
          users={users}
          onCreateProject={async (project) => {
            await handleCreateProject(project);
          }}
          onClose={() => setShowCreateForm(false)}
        />
      )}

      {showProjectDetails && selectedProject && (
        <ProjectDetails
          project={selectedProject}
          users={users}
          currentUser={currentUser}
          onUpdateProject={handleUpdateProject}
          onClose={() => setShowProjectDetails(false)}
        />
      )}

      {showClientDialog && clientDialogProject && (
        <ClientCreationDialog
          open={showClientDialog}
          onClose={() => {
            setShowClientDialog(false);
            setClientDialogProject(null);
          }}
          project={clientDialogProject}
          onClientCreated={(client) => {
            onCreateUser?.(client);
            onUpdateProject?.({
              ...clientDialogProject,
              clientId: client.id,
              clientName: client.name,
            });
            setLocalClientAssignedProjectIds((prev) =>
              new Set([...prev, clientDialogProject.id])
            );
            setShowClientDialog(false);
            setClientDialogProject(null);
          }}
        />
      )}
    </div>
  );
}
